import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import "./Register.css";
import { fetchRegistrationData, validateCityZip, validateNicDob, validateNicGender } from "../services/userService";
import NavUser from "../components/NavUser";
import { FaArrowLeft, FaHotel, FaIdCard, FaFileAlt, FaCheckCircle, FaCloudUploadAlt } from "react-icons/fa";

function TravelerToHotelOwnerRegister() {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_API_URL;

  // Form States
  const [businessName, setBusinessName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [businessOwnerName, setBusinessOwnerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [dob, setDob] = useState("");

  const [selectedPropertyType, setSelectedPropertyType] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Dropdown Data
  const [propertyTypes, setPropertyTypes] = useState([]);

  // Files
  const [nicFront, setNicFront] = useState(null);
  const [nicBack, setNicBack] = useState(null);
  const [billFront, setBillFront] = useState(null);
  const [billBack, setBillBack] = useState(null);
  const [businessReg, setBusinessReg] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isRestrictedNonSriLankan, setIsRestrictedNonSriLankan] = useState(false);

  useEffect(() => {
    async function loadUserDetails() {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (!storedUser || !storedUser.id) return;

        const res = await fetch(`${BACKEND_URL}/users/${storedUser.id}`);
        const data = await res.json();

        localStorage.setItem("full_user", JSON.stringify(data));
        setBusinessOwnerName(data.full_name || "");
        setPhoneNumber(data.phone_number || "");
        setDob(data.dob ? data.dob.split("T")[0] : "");
        setSelectedGender(data.gender_id || "");

        // Nationality / Country verification: Only Sri Lankan travelers can register as hotel owners
        const countryName = (data.Country?.name || "").toLowerCase();
        if (
          data.country_id &&
          data.country_id !== 1 &&
          !countryName.includes("sri lanka") &&
          countryName.length > 0 &&
          !data.national_id_number &&
          window.location.hostname !== "localhost" &&
          window.location.hostname !== "127.0.0.1"
        ) {
          setIsRestrictedNonSriLankan(true);
        }
        
        // Fetch existing hotel/owner details if they exist (for correcting issues)
        const detailsRes = await fetch(`${BACKEND_URL}/traveler-to-hotel-owner/my-details/${storedUser.id}`);
        if (detailsRes.ok) {
          const detailsData = await detailsRes.json();
          if (detailsData.hotel) {
            setBusinessName(detailsData.hotel.name || "");
            setAddress(detailsData.hotel.address || "");
            setZipCode(detailsData.hotel.zip_code || "");
            setCity(detailsData.hotel.city || ""); // Note: city might not be in DB but was in state
            setSelectedPropertyType(detailsData.hotel.property_type_id || "");
          }
        }
      } catch (err) {
        console.error("Failed to load user data", err);
      }
    }
    loadUserDetails();
  }, [BACKEND_URL]);

  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSingleStepUpdate, setIsSingleStepUpdate] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetStep = parseInt(params.get("step"));
    if (targetStep >= 1 && targetStep <= 3) {
      setCurrentStep(targetStep);
      setIsSingleStepUpdate(true);
    }
  }, [location.search]);

  const handleNext = () => setCurrentStep(prev => Math.min(prev + 1, 3));
  const handlePrev = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  useEffect(() => {
    async function loadDropdowns() {
      try {
        const { propertyTypes } = await fetchRegistrationData();
        setPropertyTypes(propertyTypes);
      } catch (err) {
        Swal.fire({ title: "Error", text: "Failed to load dropdown data", icon: "error" });
      }
    }
    loadDropdowns();
  }, []);

  const handleFilePick = (e, setter) => {
    if (e.target.files && e.target.files[0]) setter(e.target.files[0]);
  };

  const uploadFile = async (file, folderName) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append("destination", folderName);
    formData.append("file", file);
    const res = await fetch(`${BACKEND_URL}/upload`, { method: "POST", body: formData });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "File upload failed");
    return data.path;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!isSingleStepUpdate && currentStep < 3) {
      handleNext();
      return;
    }

    const user = JSON.parse(localStorage.getItem("full_user"));
    if (!user) {
      setErrorMessage("Please log in to continue.");
      return;
    }

    setIsSubmitting(true);

    try {
      if (!isSingleStepUpdate || currentStep === 1) {
        const cityZipValid = await validateCityZip(city, zipCode);
        if (!cityZipValid.isValid) {
          setErrorMessage(cityZipValid.message || "Invalid city and zip code combination.");
          setIsSubmitting(false);
          return;
        }

        const hotelPayload = {
          user_id: user.id,
          name: businessName,
          address: address,
          zip_code: zipCode,
          property_type_id: Number(selectedPropertyType),
          city: city,
        };

        const hotelResponse = await fetch(`${BACKEND_URL}/traveler-to-hotel-owner/hotel`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(hotelPayload),
        });

        const hotelData = await hotelResponse.json();
        if (!hotelResponse.ok || !hotelData.success) {
          throw new Error(hotelData.message || "Failed to create hotel profile");
        }
      }

      if (!isSingleStepUpdate || currentStep === 2 || currentStep === 3) {
        if (!isSingleStepUpdate || currentStep === 2) {
          const nicDobValid = await validateNicDob(nationalId, dob);
          if (!nicDobValid) {
            setErrorMessage("National ID and Date of Birth do not match.");
            setIsSubmitting(false);
            return;
          }

          const nicGenderValid = await validateNicGender(nationalId, selectedGender);
          if (!nicGenderValid) {
            setErrorMessage("National ID and selected Gender do not match.");
            setIsSubmitting(false);
            return;
          }

          // Update User profile details (name, NIC, DOB, gender)
          const userUpdateResponse = await fetch(`${BACKEND_URL}/users/${user.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              full_name: businessOwnerName,
              phone_number: phoneNumber,
              national_id_number: nationalId,
              dob: dob,
              gender_id: selectedGender
            }),
          });

          if (!userUpdateResponse.ok) {
            const userData = await userUpdateResponse.json();
            throw new Error(userData.message || "Failed to update user profile details");
          }
        }

        const uploadedNicFront = nicFront ? await uploadFile(nicFront, "nicDoc") : null;
        const uploadedNicBack = nicBack ? await uploadFile(nicBack, "nicDoc") : null;
        const uploadedBillFront = billFront ? await uploadFile(billFront, "hotelOwner") : null;
        const uploadedBillBack = billBack ? await uploadFile(billBack, "hotelOwner") : null;
        const uploadedBusinessReg = businessReg ? await uploadFile(businessReg, "hotelBusinessRegistrationDoc") : null;

        const ownerDetailsPayload = {
          user_id: user.id,
          nic_or_driving_licence_front: uploadedNicFront,
          nic_or_driving_licence_back: uploadedNicBack,
          water_or_light_bill_front: uploadedBillFront,
          water_or_light_bill_back: uploadedBillBack,
          business_registration_or_qualifications: uploadedBusinessReg,
          status_id: 1,
          is_verified: false,
          is_active: true,
        };

        const ownerDetailsResponse = await fetch(`${BACKEND_URL}/traveler-to-hotel-owner/hotel-owner-details`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ownerDetailsPayload),
        });

        const ownerData = await ownerDetailsResponse.json();
        if (!ownerDetailsResponse.ok || !ownerData.success) {
          throw new Error(ownerData.message || "Failed to save ownership details");
        }
      }

      // Update local storage user state to Hotel Owner (3) with status Pending (3)
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const updatedUserObj = { ...storedUser, ...user, role_id: 3, status_id: 3 };
      localStorage.setItem("user", JSON.stringify(updatedUserObj));
      localStorage.setItem("full_user", JSON.stringify(updatedUserObj));

      if (isSingleStepUpdate) {
        // Send email notification that the issue was fixed
        await fetch(`${BACKEND_URL}/traveler-to-hotel-owner/notify-update`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          }
        });

        Swal.fire({
          title: "Update Success!",
          text: "Your information has been updated. We have notified the admin to review your changes.",
          icon: "success",
          confirmButtonColor: "#3b82f6"
        }).then(() => {
          navigate("/explore");
        });
      } else {
        Swal.fire({
          title: "Registration Success!",
          text: "Your hotel owner profile has been submitted and is pending admin approval.",
          icon: "success",
          confirmButtonColor: "#3b82f6"
        }).then(() => {
          navigate("/explore");
        });
      }

    } catch (error) {
      Swal.fire({ title: "Registration Failed", text: error.message, icon: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="agent-page-premium">
      <NavUser />
      
      <div className="premium-hero-mini">
        <div className="hero-content">
          <h1>Grow with Soloway</h1>
          <p>List your property and reach thousands of travelers worldwide.</p>
        </div>
      </div>

      <div className="premium-container-wide">
        {isRestrictedNonSriLankan ? (
          <div className="premium-glass-card restriction-card" style={{ padding: "40px", textAlign: "center", maxWidth: "680px", margin: "40px auto", borderRadius: "16px", background: "#ffffff", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0" }}>
            <div style={{ width: "80px", height: "80px", background: "#fef2f2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
              <FaHotel size={36} color="#ef4444" />
            </div>
            <h2 style={{ fontSize: "24px", fontWeight: "800", color: "#1e293b", marginBottom: "12px" }}>
              Restricted to Sri Lankan Residents & Citizens
            </h2>
            <p style={{ fontSize: "15px", color: "#64748b", lineHeight: "1.6", marginBottom: "24px" }}>
              In accordance with Sri Lanka Tourism Development Authority (SLTDA) guidelines, hotel and accommodation ownership registrations on Soloway are exclusively reserved for Sri Lankan registered business owners and residents.
            </p>
            <div style={{ background: "#f8fafc", borderRadius: "12px", padding: "16px 20px", marginBottom: "30px", textAlign: "left", border: "1px solid #e2e8f0" }}>
              <p style={{ fontSize: "14px", color: "#334155", margin: 0, fontWeight: "600" }}>
                🌍 <strong>As an International Traveler, you can:</strong>
              </p>
              <ul style={{ fontSize: "13.5px", color: "#64748b", marginTop: "8px", paddingLeft: "20px", lineHeight: "1.6" }}>
                <li>Book verified boutique hotels, villas, and luxury resorts across Sri Lanka</li>
                <li>Hire government-certified tour guides and chauffeured transport</li>
                <li>Apply for your Sri Lanka Electronic Travel Authorization (ETA)</li>
              </ul>
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button className="btn-premium-action" onClick={() => navigate(-1)} style={{ padding: "12px 24px" }}>
                ← Return to Dashboard
              </button>
            </div>
          </div>
        ) : (
        <div className="premium-glass-card">
          <div className="card-header-premium">
            <button onClick={() => navigate(-1)} className="btn-back-minimal">
              <FaArrowLeft />
            </button>
            <div className="header-titles">
              <h2>Hotel Owner Registration</h2>
              <p>Step {currentStep} of 3: {currentStep === 1 ? "Property Information" : currentStep === 2 ? "Owner Details" : "Verification Documents"}</p>
            </div>
          </div>
          
          {!isSingleStepUpdate && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px', gap: '10px' }}>
             {[1, 2, 3].map(s => (
               <div key={s} style={{ width: '30%', height: '4px', background: s <= currentStep ? '#f97316' : '#e5e7eb', borderRadius: '2px', transition: 'background 0.3s' }}></div>
             ))}
          </div>
          )}

          <form className="premium-form-layout" onSubmit={handleSubmit}>
            <div className="form-grid-2">
              {currentStep === 1 && (
              <div className="form-column">
                <div className="section-header-minimal">
                  <FaHotel /> <span>Property Details</span>
                </div>
                
                <div className="premium-field">
                  <label>Business / Hotel Name</label>
                  <input
                    type="text"
                    placeholder="Enter official property name"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Property Type</label>
                    <select value={selectedPropertyType} onChange={(e) => setSelectedPropertyType(e.target.value)} required>
                      <option value="">Select Type</option>
                      {propertyTypes.map((pt) => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
                    </select>
                  </div>
                  <div className="premium-field">
                    <label>City</label>
                    <input type="text" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} required />
                  </div>
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>Zip Code</label>
                    <input type="text" placeholder="Zip" value={zipCode} onChange={(e) => setZipCode(e.target.value)} required />
                  </div>
                  <div className="premium-field">
                    <label>Full Address</label>
                    <input type="text" placeholder="Street, Building" value={address} onChange={(e) => setAddress(e.target.value)} required />
                  </div>
                </div>
              </div>
              )}

              {currentStep === 2 && (
              <div className="form-column">
                <div className="section-header-minimal">
                  <FaIdCard /> <span>Owner Details</span>
                </div>

                <div className="premium-field">
                  <label>Owner Full Name</label>
                  <input type="text" value={businessOwnerName} onChange={(e) => setBusinessOwnerName(e.target.value)} required />
                </div>

                <div className="form-grid-2" style={{ gap: '20px' }}>
                  <div className="premium-field">
                    <label>National ID Number</label>
                    <input type="text" value={nationalId} onChange={(e) => setNationalId(e.target.value)} required />
                  </div>
                  <div className="premium-field">
                    <label>Date of Birth</label>
                    <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} required />
                  </div>
                </div>
              </div>
              )}

              {currentStep === 3 && (
              <div className="form-column">
                <div className="section-header-minimal">
                  <FaFileAlt /> <span>Verification Documents</span>
                </div>
                <p style={{fontSize: '13px', color: '#666', marginBottom: '15px'}}>Note: If correcting an issue, only re-upload the files that were flagged.</p>

                <div className="upload-grid-premium">
                  <div className="premium-upload-item">
                    <label>ID Front</label>
                    <label className={`upload-zone ${nicFront ? 'active' : ''}`}>
                      <input type="file" accept="image/*,pdf" onChange={(e) => handleFilePick(e, setNicFront)} />
                      <FaCloudUploadAlt />
                      <span>{nicFront ? nicFront.name : "Choose File"}</span>
                    </label>
                  </div>
                  <div className="premium-upload-item">
                    <label>ID Back</label>
                    <label className={`upload-zone ${nicBack ? 'active' : ''}`}>
                      <input type="file" accept="image/*,pdf" onChange={(e) => handleFilePick(e, setNicBack)} />
                      <FaCloudUploadAlt />
                      <span>{nicBack ? nicBack.name : "Choose File"}</span>
                    </label>
                  </div>
                  <div className="premium-upload-item">
                    <label>Utility Bill (Front)</label>
                    <label className={`upload-zone ${billFront ? 'active' : ''}`}>
                      <input type="file" accept="image/*,pdf" onChange={(e) => handleFilePick(e, setBillFront)} />
                      <FaCloudUploadAlt />
                      <span>{billFront ? billFront.name : "Choose File"}</span>
                    </label>
                  </div>
                  <div className="premium-upload-item">
                    <label>Utility Bill (Back)</label>
                    <label className={`upload-zone ${billBack ? 'active' : ''}`}>
                      <input type="file" accept="image/*,pdf" onChange={(e) => handleFilePick(e, setBillBack)} />
                      <FaCloudUploadAlt />
                      <span>{billBack ? billBack.name : "Choose File"}</span>
                    </label>
                  </div>
                </div>

                <div className="premium-field full-width" style={{ marginTop: '20px' }}>
                  <label>Business Registration (Optional)</label>
                  <label className={`upload-zone-wide ${businessReg ? 'active' : ''}`}>
                    <input type="file" accept="image/*,pdf" onChange={(e) => handleFilePick(e, setBusinessReg)} />
                    <FaCloudUploadAlt />
                    <span>{businessReg ? businessReg.name : "Upload supporting registration (PDF/Image)"}</span>
                  </label>
                </div>
              </div>
              )}
            </div>
            
            {errorMessage && (
              <div className="form-error-premium" style={{ marginTop: '20px', padding: '12px', background: '#fee2e2', color: '#b91c1c', borderRadius: '10px', fontSize: '14px', textAlign: 'center' }}>
                {errorMessage}
              </div>
            )}

            <div className="action-row-centered" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px', width: '100%' }}>
              {!isSingleStepUpdate && currentStep > 1 ? (
                <button type="button" onClick={handlePrev} className="btn-premium-action" style={{ background: '#eee', color: '#333' }}>
                  Back
                </button>
              ) : <div style={{width: '100px'}}></div>}
              
              <button
                type="submit"
                className="btn-premium-action"
                disabled={isSubmitting}
                onClick={(e) => {
                  if (!isSingleStepUpdate && currentStep < 3) {
                    e.preventDefault();
                    handleNext();
                  }
                }}
              >
                {isSubmitting ? (
                  <span className="loader-dots">Processing...</span>
                ) : isSingleStepUpdate ? (
                  <>
                    <FaCheckCircle /> Update Details
                  </>
                ) : currentStep < 3 ? (
                  "Next Step"
                ) : (
                  <>
                    <FaCheckCircle /> Submit Registration
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        )}
      </div>
    </div>
  );
}

export default TravelerToHotelOwnerRegister;
